import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { employerAPI, flagsAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  User, Calendar, Star, ChevronLeft, Loader2, CheckCircle, Clock, Flag, 
  AlertCircle, Send, Building, ClipboardList, Award, Check
} from 'lucide-react';

const statusConfig = {
  acknowledged: { label: 'Acknowledged', class: 'status-verified', icon: CheckCircle },
  pending_acknowledgment: { label: 'Pending', class: 'status-pending', icon: Clock },
  draft: { label: 'Draft', class: 'status-draft', icon: Building },
  flagged: { label: 'Flagged', class: 'status-flagged', icon: Flag },
};

const RecordDetail = () => {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyMessages, setReplyMessages] = useState({});
  const [sendingReply, setSendingReply] = useState(null);

  useEffect(() => {
    fetchRecord();
  }, [id]);

  const fetchRecord = async () => {
    try {
      const res = await employerAPI.getRecord(id);
      setRecord(res.data);
    } catch (error) {
      console.error('Error fetching record:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReplyToFlag = async (flagId) => {
    const message = replyMessages[flagId];
    if (!message?.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSendingReply(flagId);
    try {
      await flagsAPI.addMessage(flagId, message);
      setReplyMessages(prev => ({ ...prev, [flagId]: '' }));
      fetchRecord();
      toast.success('Reply sent');
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(null);
    }
  };

  const handleResolveFlag = async (flagId) => {
    try {
      await flagsAPI.resolve(flagId);
      fetchRecord();
      toast.success('Flag resolved');
    } catch (error) {
      toast.error('Failed to resolve flag');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!record) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0A0F1E]">Record not found</h2>
        </div>
      </DashboardLayout>
    );
  }

  const status = statusConfig[record.status] || statusConfig.draft;

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-6" data-testid="employer-record-detail">
        {/* Back Link */}
        <Link 
          to="/employer/records" 
          className="inline-flex items-center gap-2 text-[#5B6472] hover:text-[#0A0F1E] transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Records
        </Link>

        {/* Flagged Banner */}
        {record.status === 'flagged' && (
          <Card className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Flag className="w-6 h-6 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-red-400 font-medium">This employee has raised flags on their record</p>
                <p className="text-red-400/80 text-sm">Review and respond to their concerns below.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Header */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {record.employee_photo ? (
                <img src={record.employee_photo} alt="" className="w-16 h-16 rounded-full object-cover border border-[#E8EDF5]" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#F5F8FF] border border-[#E8EDF5] flex items-center justify-center">
                  <User className="w-6 h-6 text-[#5B6472]" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-[#0A0F1E]">
                  {record.employee_name || record.employee_email}
                </h1>
                <p className="text-[#5B6472]">{record.job_title}</p>
                <div className="flex items-center gap-2 mt-1 text-[#5B6472] text-sm">
                  <Calendar className="w-4 h-4" />
                  {record.start_date} - {record.currently_employed ? 'Present' : record.end_date}
                </div>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${status.class}`}>
              <status.icon className="w-4 h-4" />
              {status.label}
            </span>
          </div>
          
          {/* Start Review button for acknowledged records */}
          {record.status === 'acknowledged' && record.employee_id && (
            <div className="mt-4 pt-4 border-t border-[#E8EDF5]">
              <Link to={`/employer/reviews/new?record_id=${record.id}&employee_id=${record.employee_id}`}>
                <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl" data-testid="start-review-btn">
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Start Performance Review
                </Button>
              </Link>
            </div>
          )}
        </Card>

        {/* Skills */}
        {record.skills?.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Skills Demonstrated</h2>
            <div className="flex flex-wrap gap-2">
              {record.skills.map((skill, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-[#F5F8FF] text-[#0A0F1E] text-sm border border-[#E8EDF5]">
                  {skill}
                </span>
              ))}
            </div>
          </Card>
        )}

        {/* Daily Responsibilities */}
        {record.daily_responsibilities?.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Daily Responsibilities</h2>
            <ul className="space-y-2">
              {record.daily_responsibilities.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-[#0A0F1E]">
                  <Check className="w-5 h-5 text-[#1B5AFF] flex-shrink-0 mt-0.5" />
                  <span className="whitespace-pre-wrap">{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Key Achievements */}
        {record.key_achievements?.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Key Achievements</h2>
            <ul className="space-y-2">
              {record.key_achievements.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-[#0A0F1E]">
                  <Award className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span className="whitespace-pre-wrap">{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Collaboration Style */}
        {(record.collaboration_style || record.collaboration_note) && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-3">Collaboration Style</h2>
            {record.collaboration_style && (
              <span className="inline-flex px-3 py-1.5 rounded-full bg-blue-500/20 text-blue-400 text-sm mb-3">
                {record.collaboration_style}
              </span>
            )}
            {record.collaboration_note && (
              <p className="text-[#0A0F1E] whitespace-pre-wrap mt-2">{record.collaboration_note}</p>
            )}
          </Card>
        )}

        {/* Performance Ratings */}
        {(record.punctuality_rating || record.work_ethic_rating) && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Performance Ratings</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {record.punctuality_rating && (
                <div>
                  <p className="text-[#5B6472] text-sm mb-2">Punctuality & Reliability</p>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-5 h-5 ${i < record.punctuality_rating ? 'fill-[#1B5AFF] text-[#1B5AFF]' : 'text-[#5B6472]'}`} />
                    ))}
                  </div>
                  {record.punctuality_note && <p className="text-[#5B6472] text-sm mt-2">{record.punctuality_note}</p>}
                </div>
              )}
              {record.work_ethic_rating && (
                <div>
                  <p className="text-[#5B6472] text-sm mb-2">Work Ethic</p>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-5 h-5 ${i < record.work_ethic_rating ? 'fill-[#1B5AFF] text-[#1B5AFF]' : 'text-[#5B6472]'}`} />
                    ))}
                  </div>
                  {record.work_ethic_note && <p className="text-[#5B6472] text-sm mt-2">{record.work_ethic_note}</p>}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Strengths & Growth */}
        <div className="grid md:grid-cols-2 gap-4">
          {record.strengths && (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <h2 className="text-lg font-semibold text-[#0A0F1E] mb-3">Top Strengths</h2>
              <p className="text-[#0A0F1E] whitespace-pre-wrap">{record.strengths}</p>
            </Card>
          )}
          {record.growth_areas && (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <h2 className="text-lg font-semibold text-[#0A0F1E] mb-3">Areas of Growth</h2>
              <p className="text-[#0A0F1E] whitespace-pre-wrap">{record.growth_areas}</p>
            </Card>
          )}
        </div>

        {/* Rehire & Rating - Only show for past employees */}
        {!record.currently_employed && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-3">Rehire Eligibility</h2>
            <span className={`inline-flex px-4 py-2 rounded-xl text-sm font-medium ${
              record.rehire_eligibility === 'yes' ? 'bg-green-500/20 text-green-400' :
              record.rehire_eligibility === 'conditional' ? 'bg-amber-500/20 text-amber-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              {record.rehire_eligibility === 'yes' ? 'Yes' : 
               record.rehire_eligibility === 'conditional' ? 'Conditional' : 'No'}
            </span>
            {record.rehire_condition && (
              <p className="text-[#5B6472] mt-2 text-sm">{record.rehire_condition}</p>
            )}
          </Card>
          {record.rating && (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <h2 className="text-lg font-semibold text-[#0A0F1E] mb-3">Overall Rating</h2>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-8 h-8 ${i < record.rating ? 'fill-[#1B5AFF] text-[#1B5AFF]' : 'text-[#5B6472]'}`} 
                  />
                ))}
              </div>
            </Card>
          )}
        </div>
        )}

        {/* Reason for Departure */}
        {!record.currently_employed && (record.reason_for_departure || record.departure_note) && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-3">Reason for Departure</h2>
            {record.reason_for_departure && (
              <span className="inline-flex px-3 py-1.5 rounded-full bg-[#F5F8FF] text-[#0A0F1E] text-sm border border-[#E8EDF5] mb-3">
                {record.reason_for_departure}
              </span>
            )}
            {record.departure_note && (
              <p className="text-[#0A0F1E] whitespace-pre-wrap mt-2">{record.departure_note}</p>
            )}
          </Card>
        )}

        {/* Professional Closing Statement */}
        {record.closing_statement && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-3">Professional Closing Statement</h2>
            <p className="text-[#0A0F1E] whitespace-pre-wrap italic">{record.closing_statement}</p>
          </Card>
        )}

        {/* Currently Employed Status */}
        {record.currently_employed && (
          <Card className="bg-green-500/10 border border-green-500/20 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <Check className="w-6 h-6 text-green-400" />
              <div>
                <h2 className="text-lg font-semibold text-green-400">Currently Employed</h2>
                <p className="text-[#5B6472] text-sm">This employee is still actively employed at this company.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Public Note */}
        {record.public_note && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-3">Public Note</h2>
            <p className="text-[#0A0F1E]">{record.public_note}</p>
          </Card>
        )}

        {/* Flags */}
        {record.flags?.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4 flex items-center gap-2">
              <Flag className="w-5 h-5 text-[#1B5AFF]" />
              Employee Flags
            </h2>
            <div className="space-y-6">
              {record.flags.map((flag) => (
                <div key={flag.id} className="p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5]">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-[#0A0F1E] capitalize">{flag.field_name?.replace(/_/g, ' ')}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      flag.status === 'resolved' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {flag.status}
                    </span>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
                    <p className="text-[#0A0F1E] text-sm">{flag.employee_message}</p>
                  </div>

                  {/* Flag Messages */}
                  {flag.messages?.length > 0 && (
                    <div className="space-y-3 mb-4">
                      {flag.messages.map((msg) => (
                        <div key={msg.id} className="flex gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            msg.sender_id === record.employer_id 
                              ? 'bg-[#1B5AFF]/20' 
                              : 'bg-[#F5F8FF] border border-[#E8EDF5]'
                          }`}>
                            {msg.sender_id === record.employer_id ? (
                              <Building className="w-4 h-4 text-[#1B5AFF]" />
                            ) : (
                              <User className="w-4 h-4 text-[#5B6472]" />
                            )}
                          </div>
                          <div className="flex-1 p-3 rounded-lg bg-[#F5F8FF]/50 border border-[#E8EDF5]">
                            <p className="text-[#0A0F1E] text-sm">{msg.message}</p>
                            <p className="text-[#5B6472] text-xs mt-1">
                              {new Date(msg.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply & Resolve */}
                  {flag.status === 'open' && (
                    <div className="space-y-3">
                      <Textarea
                        value={replyMessages[flag.id] || ''}
                        onChange={(e) => setReplyMessages(prev => ({ ...prev, [flag.id]: e.target.value }))}
                        placeholder="Type your response..."
                        className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[80px]"
                      />
                      <div className="flex justify-end gap-2">
                        <Button
                          onClick={() => handleResolveFlag(flag.id)}
                          variant="outline"
                          className="border-green-500/30 text-green-400 hover:bg-green-500/10 rounded-xl"
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Mark Resolved
                        </Button>
                        <Button
                          onClick={() => handleReplyToFlag(flag.id)}
                          disabled={sendingReply === flag.id}
                          className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
                        >
                          {sendingReply === flag.id ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="mr-2 h-4 w-4" />
                          )}
                          Reply
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default RecordDetail;
